// GLTF Model loader with caching

import * as THREE from 'three'
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { debug } from '@/utils/debug'

export interface ModelDimensions {
  width: number   // X-axis extent (meters)
  height: number  // Y-axis extent (meters)
  depth: number   // Z-axis extent (meters)
}

interface CachedModel {
  scene: THREE.Group
  dimensions: ModelDimensions
  geometry?: THREE.BufferGeometry
  material?: THREE.Material
}

export class ModelLoader {
  private loader: GLTFLoader
  private cache: Map<string, CachedModel> = new Map()
  private loadingPromises: Map<string, Promise<CachedModel>> = new Map()

  constructor() {
    this.loader = new GLTFLoader()

    // Setup DRACO loader for compressed models (optional)
    const dracoLoader = new DRACOLoader()
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/')
    this.loader.setDRACOLoader(dracoLoader)
  }

  /**
   * Load a GLTF/GLB model from URL
   * Returns cached version if already loaded
   */
  async load(url: string): Promise<CachedModel> {
    // Return from cache if available
    if (this.cache.has(url)) {
      return this.cache.get(url)!
    }

    // Return existing loading promise if in progress
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url)!
    }

    // Start loading
    const loadPromise = this.loadModel(url)
    this.loadingPromises.set(url, loadPromise)

    try {
      const model = await loadPromise
      this.cache.set(url, model)
      return model
    } finally {
      this.loadingPromises.delete(url)
    }
  }

  /**
   * Preload multiple models in parallel
   * Uses Promise.allSettled so one failure doesn't stop others
   */
  async preloadMany(urls: string[]): Promise<void> {
    const results = await Promise.allSettled(urls.map(url => this.load(url)))

    // Log summary of results
    const succeeded = results.filter(r => r.status === 'fulfilled').length
    const failed = results.filter(r => r.status === 'rejected').length
    if (failed > 0) {
      debug.warn('Model', `Preload: ${succeeded} succeeded, ${failed} failed (will use fallbacks)`)
    }
  }

  /**
   * Create a clone of a loaded model
   */
  async createInstance(url: string): Promise<THREE.Group> {
    const model = await this.load(url)
    return model.scene.clone()
  }

  /**
   * Check if a model is already cached
   */
  isCached(url: string): boolean {
    return this.cache.has(url)
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    // Dispose of all cached geometries and materials
    for (const model of this.cache.values()) {
      model.geometry?.dispose()
      model.material?.dispose()
    }
    this.cache.clear()
  }

  private async loadModel(url: string): Promise<CachedModel> {
    // Extract model ID from URL for logging
    const modelId = url.split('/').pop() || url

    return new Promise((resolve, reject) => {
      this.loader.load(
        url,
        (gltf: GLTF) => {
          const scene = gltf.scene

          // Enable shadows for all meshes
          scene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              child.castShadow = true
              child.receiveShadow = true
            }
          })

          // Skip bounding box analysis - assume models are properly centered and sized
          const dimensions: ModelDimensions = { width: 1, height: 1, depth: 1 }

          debug.log('Model', `${modelId}: loaded`)

          resolve({ scene, dimensions })
        },
        undefined, // Progress callback
        (error) => {
          debug.error('Model', `Failed to load: ${url}`, error)
          reject(error)
        }
      )
    })
  }

  /**
   * Get dimensions of a loaded model
   */
  getModelDimensions(url: string): ModelDimensions | null {
    const model = this.cache.get(url)
    return model?.dimensions ?? null
  }
}

// Singleton instance
export const modelLoader = new ModelLoader()
